import {
	getCalendarDaysUntil,
	toLocalCalendarDate,
} from "@memorang/helpers/date";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pressable } from "react-native";
import { Text, TextInput } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import { ExamDatePickerModal } from "../ExamDatePickerModal";
import { useOnboarding } from "./OnboardingProvider";
import { ChangeLabel } from "./components/ChangeLabel";
import { OnboardingBox } from "./components/OnboardingBox";

type Props = {
	title: string;
	description: string | undefined;
};

const formatDate = (date: Date) =>
	date.toLocaleDateString(undefined, {
		month: "2-digit",
		day: "2-digit",
		year: "numeric",
	});

export const OnboardingExamDateScreen = ({ title, description }: Props) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	const { onExamDateChange, selectedExamDate } = useOnboarding();
	const defaultDate =
		toLocalCalendarDate(selectedExamDate) ??
		(() => {
			const d = new Date();
			d.setDate(d.getDate() + 30);
			return d;
		})();
	const [selectedDate, setSelectedDate] = useState<Date>(defaultDate);
	const [showPicker, setShowPicker] = useState(false);
	const initialDateRef = useRef(defaultDate);
	const onExamDateChangeRef = useRef(onExamDateChange);

	useEffect(() => {
		onExamDateChangeRef.current?.(initialDateRef.current);
	}, []);

	const handleConfirm = (date: Date) => {
		setSelectedDate(date);
		setShowPicker(false);
		onExamDateChange?.(date);
	};

	const daysAway = selectedDate
		? (getCalendarDaysUntil(selectedDate) ?? null)
		: null;

	return (
		<>
			<Box gap={10}>
				<OnboardingBox>
					<Box gap={14}>
						<Text variant="titleLarge">{title}</Text>
						{description ? (
							<Text variant="bodyMedium">{description}</Text>
						) : null}
					</Box>
					<Box gap={10}>
						<Pressable onPress={() => setShowPicker(true)}>
							<TextInput
								mode="outlined"
								value={selectedDate ? formatDate(selectedDate) : ""}
								editable={false}
								pointerEvents="none"
								outlineColor={theme.colors.surfaceVariant}
								right={
									<TextInput.Icon
										icon="calendar"
										onPress={() => setShowPicker(true)}
									/>
								}
								style={{ backgroundColor: theme.colors.surface }}
							/>
						</Pressable>
						{daysAway !== null && daysAway > 0 ? (
							<Box
								padding={16}
								borderRadius={theme.roundness * 3}
								borderWidth={1}
								borderColor={theme.colors.surfaceVariant}
								backgroundColor={theme.colors.background}
							>
								<Text variant="bodyMedium">
									{t("onboarding.steps.examDate.daysAway", { value: daysAway })}
								</Text>
							</Box>
						) : null}
					</Box>
				</OnboardingBox>
				<ChangeLabel />
			</Box>
			{showPicker ? (
				<ExamDatePickerModal
					isVisible
					onConfirm={handleConfirm}
					onCancel={() => setShowPicker(false)}
					label={title}
					ctaLabel={t("onboarding.steps.testDate.cta")}
					initialDate={selectedDate.toISOString()}
				/>
			) : null}
		</>
	);
};
